export enum Role {
  ADMIN = 'admin',
  ORGANIZER = 'organizer',
  CONTENT_CREATOR = 'contentCreator'
}

export enum ProtectPageOption {
  RequireAdmin = 'requireAdmin',
  RequireAdminOrContentCreator = 'requireAdminOrContentCreator',
  CanSendBroadcasts = 'canSendBroadcasts',
  CanSendBroadcastsOrContentCreator = 'canSendBroadcastsOrContentCreator',
  DenyContentCreator = 'denyContentCreator'
}

// An admin whose row carries the contentCreator role string is still an admin.
export const isContentCreator = (
  user: { isAdmin?: boolean; role?: string } | null | undefined
) => !user?.isAdmin && user?.role === Role.CONTENT_CREATOR

// Inbox conversation assignment — the "Assign Conversation" bulk action, the
// Assigned To column and the per-conversation assignee dropdown — is an
// admin-level capability that content creators share. Organizers reach it only
// through an organizer_assignment_permissions row, so callers OR this with
// their own permission check rather than replacing it.
export const hasFullConversationAssignmentAccess = (
  user: { isAdmin?: boolean; role?: string } | null | undefined
) => !!user?.isAdmin || isContentCreator(user)

// Member field visibility and editability — the inbox contact summary, the
// member pane, the inbox bulk "edit field" action and the member change log.
// Content creators own the Members page and the custom-fields CRUD routes and
// hold a content_creator_update policy on app_public.members, so they resolve
// fields the way admins do rather than falling through the organizer branch,
// which would hand them a view-only core and no custom fields at all.
//
// A separate predicate from hasFullConversationAssignmentAccess despite the
// identical body today: these are two capabilities that happen to be granted to
// the same roles, and collapsing them would tie a future change in one to the
// other.
export const hasFullMemberFieldAccess = (
  user: { isAdmin?: boolean; role?: string } | null | undefined
) => !!user?.isAdmin || isContentCreator(user)

// The one definition of what each ProtectPageOption requires. Admins pass every
// option; each entry decides whether a NON-admin is denied. Both guards read
// this — protectPage (redirect) and AuthorizedAccess (render) — so a page cannot
// end up redirecting on one rule and rendering on another.
const denyNonAdminForOption: Record<
  ProtectPageOption,
  (user: { role?: string; can_send_broadcasts?: boolean }) => boolean
> = {
  [ProtectPageOption.RequireAdmin]: () => true,
  [ProtectPageOption.RequireAdminOrContentCreator]: (user) =>
    user.role !== Role.CONTENT_CREATOR,
  [ProtectPageOption.CanSendBroadcasts]: (user) => !user.can_send_broadcasts,
  [ProtectPageOption.CanSendBroadcastsOrContentCreator]: (user) =>
    !user.can_send_broadcasts && user.role !== Role.CONTENT_CREATOR,
  [ProtectPageOption.DenyContentCreator]: (user) =>
    user.role === Role.CONTENT_CREATOR
}

// True when the user satisfies the option. No option means no role requirement.
export const satisfiesProtectPageOption = (
  option: ProtectPageOption | null | undefined,
  user:
    | { isAdmin?: boolean; role?: string; can_send_broadcasts?: boolean }
    | null
    | undefined
) => {
  if (!option) return true
  if (!user) return false
  if (user.isAdmin) return true
  return !denyNonAdminForOption[option]?.(user)
}
